import React from 'react';
import { levelerStateLabel } from './levelerIcons';
import useIsMobile from '../hooks/useIsMobile';
import MobileGateCard from './MobileGateCard';

function gateStatusColor(g) {
  if (g.is_open) return 'red';
  if (g.cycle_limit && g.cycle_count / g.cycle_limit >= 0.9) return 'yellow';
  return 'green';
}

function formatLevelerElapsed(changedAt) {
  if (!changedAt) return null;
  const ms = Date.now() - new Date(changedAt).getTime();
  if (ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} דק'`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem ? `${hours} ש' ${rem} דק'` : `${hours} ש'`;
}

function LevelerChip({ gate }) {
  const label = levelerStateLabel(gate);
  if (!label) return <span style={{ color: '#9ca3af' }}>—</span>;
  const working = gate.leveler_state === 'working';
  const bg = working ? '#fff7ed' : '#f0fdf4';
  const border = working ? '#fb923c' : '#86efac';
  const fg = working ? '#9a3412' : '#166534';
  const elapsed = formatLevelerElapsed(gate.leveler_state_changed_at);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.15rem 0.55rem',
        borderRadius: '999px',
        background: bg,
        border: `1px solid ${border}`,
        color: fg,
        fontSize: '0.78rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {elapsed && (
        <span style={{ color: fg, opacity: 0.7, fontWeight: 500 }}>· {elapsed}</span>
      )}
    </span>
  );
}

/**
 * Read-only table of gates with optional checkbox-based bulk selection.
 *
 * Used both on the customer's site page (live overview) and on the
 * admin's site page when not in edit mode. Edit-mode actions like
 * delete-row are passed in via `extraColumns` so the same table works
 * for both surfaces without baking admin-only concerns into it.
 *
 * Selection is always available — checking a row contributes to the
 * shared `selectedGateIds` state up the tree, which the bottom
 * actions bar reads to know whether to show itself.
 */
function GatesTable({
  gates,
  selectedIds = [],
  onToggleSelect,
  onRowClick,
  extraHeaderCells = null,
  renderExtraCells = null,
  // Phase 5.G PR-5 — passed through to per-card open/close button on
  // mobile. Desktop table doesn't render them (keeps the spreadsheet
  // look managers expect from a PC).
  canIssueCommands = false,
  onCommandComplete = null,
  // Phase 5.G PR-6 — per-user mobile favourites. Only used by the
  // card layout; desktop table ignores them.
  isPinned = null,
  onTogglePin = null,
}) {
  const isMobile = useIsMobile();
  const allChecked = gates.length > 0 && gates.every(g => selectedIds.includes(g.id));
  const someChecked = !allChecked && gates.some(g => selectedIds.includes(g.id));
  // Show the leveler column only when at least one row needs it —
  // sites without any dock-leveler gates shouldn't carry a column
  // that's always "—" for every row.
  const showLevelerColumn = gates.some(g => g.is_continuous_with_leveler);

  const toggleAll = () => {
    if (!onToggleSelect) return;
    if (allChecked || someChecked) {
      // Clear selection: deselect everything currently selected.
      gates.forEach(g => {
        if (selectedIds.includes(g.id)) onToggleSelect(g.id);
      });
    } else {
      gates.forEach(g => {
        if (!selectedIds.includes(g.id)) onToggleSelect(g.id);
      });
    }
  };

  // Phase 5.G PR-2 — on phones we render dedicated MobileGateCards
  // instead of squeezing the desktop <table> into per-row stacks via
  // CSS. The card layout has clearer hierarchy (name + status pill on
  // top, meta on a secondary row) and is much easier to scan with a
  // thumb. We deliberately don't render the bulk-edit "delete" button
  // from ``renderExtraCells`` here — edit-mode is desktop-only by
  // convention (you don't redesign a site layout on a phone), and the
  // CustomerSiteMap toggle already forces map view in edit mode.
  if (isMobile) {
    if (gates.length === 0) {
      return (
        <div className="biz-gate-list-empty">
          אין שערים באתר זה
        </div>
      );
    }
    return (
      <div className="biz-gate-list">
        {gates.map(g => (
          <MobileGateCard
            key={g.id}
            gate={g}
            selected={selectedIds.includes(g.id)}
            onToggleSelect={onToggleSelect}
            onClick={onRowClick}
            selectable={!!onToggleSelect}
            canIssueCommands={canIssueCommands}
            onCommandComplete={onCommandComplete}
            pinned={isPinned ? isPinned(g.id) : false}
            onTogglePin={onTogglePin}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="data-table">
      <table>
        <thead>
          <tr>
            {onToggleSelect && (
              <th style={{ width: '1%' }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={el => {
                    if (el) el.indeterminate = someChecked;
                  }}
                  onChange={toggleAll}
                  aria-label="בחר הכל"
                />
              </th>
            )}
            <th>שם</th>
            <th>דגם</th>
            <th>קבוצה</th>
            <th>סטטוס</th>
            {showLevelerColumn && <th>משווה גובה</th>}
            <th>סייקלים</th>
            <th>רכיב</th>
            {extraHeaderCells}
          </tr>
        </thead>
        <tbody>
          {gates.map(g => {
            const isChecked = selectedIds.includes(g.id);
            const handleRowClick = (e) => {
              if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
              if (onRowClick) onRowClick(g);
            };
            return (
              <tr
                key={g.id}
                onClick={handleRowClick}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  background: isChecked ? '#eff6ff' : undefined,
                }}
              >
                {onToggleSelect && (
                  <td onClick={e => e.stopPropagation()} style={{ width: '1%' }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggleSelect(g.id)}
                      aria-label={`בחר את שער ${g.name}`}
                    />
                  </td>
                )}
                <td data-label="שם">{g.name}</td>
                <td data-label="דגם">{g.model || '-'}</td>
                <td data-label="קבוצה" style={{ color: g.group_name ? '#1a1a2e' : '#9ca3af', fontSize: '0.85rem' }}>
                  {g.group_name || '-'}
                </td>
                <td data-label="סטטוס">
                  {g.installation_status === 'not_started' ? (
                    <>
                      <span className="status-dot"></span>
                      {' '}לא מוגדר
                    </>
                  ) : (
                    <>
                      <span className={`status-dot ${gateStatusColor(g)}`}></span>
                      {' '}{g.is_open ? 'פתוח' : 'סגור'}
                    </>
                  )}
                </td>
                {showLevelerColumn && (
                  <td data-label="משווה גובה">
                    <LevelerChip gate={g} />
                  </td>
                )}
                <td data-label="סייקלים">{(g.cycle_count || 0).toLocaleString()}</td>
                <td data-label="רכיב" style={{ color: g.sheli_id ? '#1a1a2e' : '#9ca3af' }}>
                  {/* Identifier of the linked Shelly, NOT a connection
                      status — for that the map icon's fade is the
                      single source of truth (driven by
                      device.is_broker_connected). Falls back to '-'
                      when the gate has no linked device, matching
                      the dgm / group columns. */}
                  {g.sheli_id || '-'}
                </td>
                {renderExtraCells && renderExtraCells(g)}
              </tr>
            );
          })}
        </tbody>
      </table>
      {gates.length === 0 && (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
          אין שערים באתר זה
        </div>
      )}
    </div>
  );
}

export default GatesTable;
